import { createFileRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import {
  Activity,
  BellRing,
  Bot,
  ArrowLeftRight,
  Coins,
  FilePen,
  KeyRound,
  Landmark,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MonitorSmartphone,
  PanelLeft,
  Rocket,
  ScrollText,
  ServerCog,
  Settings,
  ShieldCheck,
  ShieldAlert,
  Siren,
  Sparkles,
  UserRoundCog,
  UserSearch,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { useEffect } from 'react'

import { canAccessPath } from '@shared/nav'
import { CommandHint, CommandPalette } from '@/components/command-palette'
import {
  AnimatedSidebar,
  AnimatedSidebarContent,
  AnimatedSidebarFooter,
  AnimatedSidebarGroup,
  AnimatedSidebarGroupLabel,
  AnimatedSidebarHeader,
  AnimatedSidebarInset,
  AnimatedSidebarMenu,
  AnimatedSidebarMenuButton,
  AnimatedSidebarMenuItem,
  AnimatedSidebarProvider,
  AnimatedSidebarRail,
  AnimatedSidebarTrigger,
  useAnimatedSidebar,
} from '@/components/motion/animated-sidebar'
import { ThemeToggle } from '@/components/theme-toggle'
import { Spinner } from '@/components/ui/spinner'
import { authClient } from '@/lib/auth-client'
import type { Me } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useSessionQuery } from '@/lib/session'

export const Route = createFileRoute('/dashboard')({
  component: DashboardLayout,
})

/** 菜单项声明的是图标名，这里映射到组件——服务端不该关心前端用哪套图标库 */
const ICONS: Record<string, LucideIcon> = {
  LayoutDashboard,
  Activity,
  BellRing,
  Bot,
  Coins,
  FilePen,
  KeyRound,
  ArrowLeftRight,
  Wallet,
  UserSearch,
  ShieldCheck,
  MonitorSmartphone,
  ScrollText,
  ListChecks,
  Landmark,
  ServerCog,
  Siren,
  Sparkles,
  UserRoundCog,
  Rocket,
}

const SIDEBAR_STORAGE_KEY = 'launchpad-admin-sidebar'

/** 展开/收起要跨刷新记住——每次进后台都要重新收一遍的侧边栏比不能收还烦。 */
function readStoredOpen(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'collapsed'
  } catch {
    // 隐私模式下 localStorage 会抛异常，当作"没存过"，与 theme.ts 同一口径
    return true
  }
}

function persistOpen(open: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, open ? 'expanded' : 'collapsed')
  } catch {
    // 存不下就只在本次会话生效
  }
}

/**
 * 侧边栏当前是不是"图标轨"形态。
 *
 * 移动端的面板永远是完整宽度（收起时整块滑出去，而不是变窄），
 * 所以不能只看 open——那会让手机上的抽屉里只剩图标。
 */
function useRailCollapsed(): boolean {
  const { isMobile, open } = useAnimatedSidebar()
  return !isMobile && !open
}

function DashboardLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: me, isLoading, isError } = useSessionQuery()

  // 未登录 → 登录页。这是体验层守卫，真正的拦截在后端每个路由的 require() 上。
  useEffect(() => {
    if (!isLoading && (isError || !me)) {
      navigate({ to: '/login' })
    }
  }, [isLoading, isError, me, navigate])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-5 text-cyan" />
      </div>
    )
  }

  if (!me) return null

  // 菜单来自服务端，已按角色过滤过。按分组聚合，保持服务端给定的组内顺序。
  const groups = me.nav.reduce<Record<string, typeof me.nav>>((acc, item) => {
    const key = item.group ?? '其他'
    ;(acc[key] ??= []).push(item)
    return acc
  }, {})

  const allowed = canAccessPath(me.nav, location.pathname)
  const current = me.nav.find(
    (n) =>
      location.pathname === n.href ||
      (n.href !== '/dashboard' && location.pathname.startsWith(`${n.href}/`)),
  )

  return (
    <AnimatedSidebarProvider
      defaultOpen={readStoredOpen()}
      onOpenChange={persistOpen}
      // 15.5rem = 248px，与改版前的固定侧栏同宽，页面内容的横向节奏不变
      style={{ '--sidebar-width': '15.5rem' }}
    >
      <CommandPalette />

      <AnimatedSidebar
        collapsible="icon"
        ariaLabel="主导航"
        // 为什么把底色又写一遍：`chrome` 是 @utility，而组件内置的 `bg-background`
        // 在产物里排在自定义 utility 之后，同优先级下后者赢。写成 `bg-night/72`
        // 既能被 twMerge 判成同组顶掉 `bg-background`，取值又和 chrome 里的一致。
        panelClassName="chrome bg-night/72 border-line/50"
      >
        <AnimatedSidebarHeader className="h-16 flex-row items-center gap-2.5 px-5 py-0">
          <BrandMark />
        </AnimatedSidebarHeader>

        <AnimatedSidebarContent className="px-2 pb-4">
          {Object.entries(groups).map(([group, items]) => (
            <AnimatedSidebarGroup key={group}>
              <AnimatedSidebarGroupLabel className="px-3 font-mono text-[11px] tracking-[0.08em] text-mut2">
                {group}
              </AnimatedSidebarGroupLabel>

              <AnimatedSidebarMenu>
                {items.map((item) => {
                  const Icon = ICONS[item.icon] ?? LayoutDashboard
                  const active =
                    location.pathname === item.href ||
                    (item.href !== '/dashboard' && location.pathname.startsWith(`${item.href}/`))

                  return (
                    <AnimatedSidebarMenuItem key={item.href}>
                      <AnimatedSidebarMenuButton
                        href={item.href}
                        isActive={active}
                        icon={
                          <Icon className={cn('size-[17px]', active ? 'text-cyan' : 'text-mut2')} />
                        }
                        // 路由跳转交给 TanStack Router，但保留 href：
                        // 中键 / ⌘-点击 / 右键复制链接这些浏览器原生行为不该被吃掉，
                        // 所以只在"普通左键"这一种情况下才接管。
                        onSelect={(event) => {
                          if (
                            event.metaKey ||
                            event.ctrlKey ||
                            event.shiftKey ||
                            event.altKey ||
                            event.button !== 0
                          ) {
                            return
                          }
                          event.preventDefault()
                          void navigate({ to: item.href })
                        }}
                        className={cn(
                          'text-[13.5px]',
                          active ? 'text-cyan' : 'text-mut hover:text-txt',
                        )}
                        // 选中态是动作色，不是中性灰：色板契约里 cyan 就是"动作"这一档。
                        // 浅底上 10% 色块不够实，补一圈同色细描边把它钉住。
                        activePillClassName="bg-cyan/10 inset-ring inset-ring-cyan/15"
                      >
                        {item.title}
                      </AnimatedSidebarMenuButton>
                    </AnimatedSidebarMenuItem>
                  )
                })}
              </AnimatedSidebarMenu>
            </AnimatedSidebarGroup>
          ))}
        </AnimatedSidebarContent>

        {/* 账号卡片放侧边栏底部，是 macOS / iOS 设置页的惯例位置 */}
        <AnimatedSidebarFooter className="border-line/40 p-3">
          <AccountCard user={me.user} />
        </AnimatedSidebarFooter>

        {/* 面板右缘的隐形拖柄：指针扫到边上就能收放，不用去找按钮 */}
        <AnimatedSidebarRail />
      </AnimatedSidebar>

      <AnimatedSidebarInset>
        <header className="chrome sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-line/40 px-4 md:px-8">
          <div className="flex min-w-0 items-center gap-2.5">
            <AnimatedSidebarTrigger className="pressable surface-inset size-8 text-mut hover:text-txt">
              <PanelLeft className="size-4" />
            </AnimatedSidebarTrigger>
            <h1 className="truncate text-[15px] font-medium tracking-tight text-txt">
              {current?.title ?? (location.pathname.endsWith('/settings') ? '个人设置' : '控制台')}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <CommandHint />
            <ThemeToggle />
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 md:px-8 md:py-7">
          {/*
            路由守卫：直接在地址栏输入无权访问的路径时挡在这里。
            用服务端实际下发的菜单判断，而不是在前端重新按角色推导，
            所以角色菜单配置修改后「菜单里看不到」与「路径进不去」保持一致。
            这仍是体验层——真正的拦截在 API 的 require()。
          */}
          {allowed ? <Outlet /> : <Forbidden />}
        </main>
      </AnimatedSidebarInset>
    </AnimatedSidebarProvider>
  )
}

/** 品牌位。收成图标轨时只留信号点，字标淡出——轨道只有 68px，塞不下也不该塞。 */
function BrandMark() {
  const collapsed = useRailCollapsed()

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2.5',
        collapsed && 'w-full justify-center gap-0',
      )}
    >
      {/* 不做外扩发光：浅底上会糊成一团灰，深底上又会和热粉本身的高亮度打架。
          两个主题都用一圈极淡的同色扩散环，同样是"这里有信号"的意思。 */}
      <div
        aria-hidden
        className="size-2 shrink-0 rounded-full bg-cyan"
        style={{ boxShadow: '0 0 0 3px rgb(var(--cyan) / 0.16)' }}
      />
      <span
        className={cn(
          'display-title truncate text-[19px] text-txt transition-opacity duration-200',
          collapsed && 'pointer-events-none w-0 opacity-0',
        )}
      >
        Launchpad
      </span>
    </div>
  )
}

function AccountCard({ user }: { user: Me['user'] }) {
  const { isMobile, setOpenMobile } = useAnimatedSidebar()
  const collapsed = useRailCollapsed()
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false)
  }

  const avatar = (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-cyan/15 text-[13px] font-medium text-cyan">
      {user.name.slice(0, 1).toUpperCase()}
    </div>
  )

  const signOut = async () => {
    await authClient.signOut()
    window.location.href = '/login'
  }

  // 图标轨里没有横向空间放姓名和角色，退化成两个居中的图标按钮。
  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1.5">
        <Link to="/dashboard/settings" title={`${user.name} · ${roleLabel(user.role)}`}>
          {avatar}
        </Link>
        <button
          title="退出登录"
          className="pressable rounded-apple-sm p-1.5 text-mut2 hover:bg-edge/10 hover:text-rose"
          onClick={signOut}
        >
          <LogOut className="size-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="surface-inset flex items-center gap-2.5 p-2.5">
      <Link
        to="/dashboard/settings"
        onClick={closeOnMobile}
        className="flex min-w-0 flex-1 items-center gap-2.5"
        title="个人设置"
      >
        {avatar}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] leading-tight text-txt">{user.name}</div>
          <div className="truncate font-mono text-[10.5px] leading-tight text-mut2">
            {roleLabel(user.role)}
          </div>
        </div>
      </Link>

      <Link
        to="/dashboard/settings"
        onClick={closeOnMobile}
        title="个人设置"
        className="pressable rounded-apple-sm p-1.5 text-mut2 hover:bg-edge/10 hover:text-txt"
      >
        <Settings className="size-4" />
      </Link>

      <button
        title="退出登录"
        className="pressable rounded-apple-sm p-1.5 text-mut2 hover:bg-edge/10 hover:text-rose"
        onClick={signOut}
      >
        <LogOut className="size-4" />
      </button>
    </div>
  )
}

/** 无权访问时的提示。不静默重定向——让用户知道是权限问题，而不是页面坏了。 */
function Forbidden() {
  return (
    <div className="rise flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center">
      <div className="surface flex size-14 items-center justify-center rounded-apple-lg">
        <ShieldAlert className="size-6 text-warn" />
      </div>
      <h2 className="mt-2 text-xl tracking-tight text-txt">没有访问权限</h2>
      <p className="max-w-sm text-[13.5px] leading-relaxed text-mut">
        当前账号的角色无法查看这个页面。如需访问，请联系超级管理员调整角色。
      </p>
      <Link to="/dashboard" className="mt-1 text-[13.5px] text-cyan hover:underline">
        返回概览
      </Link>
    </div>
  )
}

export function roleLabel(role: string): string {
  return { admin: '超级管理员', operator: '运营', campaign_operator: '活动运营', viewer: '只读' }[role] ?? role
}
